import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import jwtDecode from 'jwt-decode';
import { socketAPI } from 'app/api';
import { getActiveToken } from 'utils/tabAuth';
import { useSelector } from 'react-redux';
import { RootState } from 'types/RootState';
import { isEmbedPath } from 'utils/embedBoot';

const embedContactRoomId = (): string | null => {
  if (!isEmbedPath()) return null;
  try {
    const token = getActiveToken();
    const encrypted =
      token &&
      !!(jwtDecode(token.replace(/^Bearer /, '')) as { encrypt?: boolean })
        .encrypt;
    return encrypted
      ? (sessionStorage.getItem('selectedClientWaNumber') ?? '')
      : null;
  } catch {
    return null;
  }
};

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType>({ socket: null });

export const useSocket = () => useContext(SocketContext);
export let socket: Socket | null = null;

export const createSocket = (roomId: string): Socket => {
  socket = io(socketAPI, {
    transports: ['websocket', 'polling', 'flashsocket'],
    secure: true,
    auth: cb => cb({ token: getActiveToken() as string }),
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    randomizationFactor: 0.5,
  });

  socket.on('connect', () => {
    if (socket) {
      console.log(`Frontend connected with id: ${socket.id}`);
      socket.emit('join_room', roomId);
    }
  });

  socket.on('disconnect', reason => {
    console.log(`Socket disconnected: ${reason}`);
    if (reason === 'io server disconnect' && socket) {
      // Reconnect manually if the server disconnects the client
      socket.connect();
    }
  });

  return socket;
};

/** Drop this tab's realtime connection (used when a tab is parked/inactive). */
export const disconnectSocket = (): void => {
  socket?.disconnect();
};

export const SocketContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const state = useSelector((state: RootState) => state?.authPage?.authData);

  useEffect(() => {
    // Don't connect socket on docs pages
    const isDocsPage = window.location.pathname.startsWith('/docs');
    if (isDocsPage) return;

    if (state?.isLoggedIn && state.business?.id) {
      const roomId = embedContactRoomId() ?? state.business.id.toString();
      if (!roomId) return;
      const newSocket = createSocket(roomId);
      setSocket(newSocket);
      return () => {
        newSocket.close();
      };
    }
  }, [state?.business?.id, state?.isLoggedIn]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};
